import { useEffect, useState } from 'react';

const BALE_SESSION_KEY = 'bale_webapp_session';

const getCachedBaleSession = () => {
  try {
    const cached = sessionStorage.getItem(BALE_SESSION_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCachedBaleSession = (session) => {
  try {
    sessionStorage.setItem(BALE_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage errors in restricted webviews.
  }
};

export const useTelegram = () => {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [startParam, setStartParam] = useState(null);
  const [isStartCommand, setIsStartCommand] = useState(false);
  const [webApp, setWebApp] = useState(null);
  const [themeParams, setThemeParams] = useState({});
  const [isTelegram, setIsTelegram] = useState(false);

  useEffect(() => {
    const cachedSession = getCachedBaleSession();

    if (cachedSession?.user) {
      setUser(cachedSession.user);
      setIsTelegram(true);
      setIsReady(true);
    }

    // تابع برای راه‌اندازی SDK بله
    const setupWebApp = () => {
      // بررسی می‌کنیم که آیا SDK بله بارگذاری شده است
      if (!window.Bale?.WebApp) {
        console.error("Bale WebApp SDK not found!");
        return;
      }
      const baleWebApp = window.Bale.WebApp;
      setWebApp(baleWebApp);

      const hasInitData = !!(baleWebApp.initData && baleWebApp.initData.length > 0);
      const hasInitDataUnsafe = !!(baleWebApp.initDataUnsafe && Object.keys(baleWebApp.initDataUnsafe).length > 0);
      const hasPlatform = !!(baleWebApp.platform && baleWebApp.platform !== 'unknown');
      const liveUser = baleWebApp.initDataUnsafe?.user;
      const cachedUser = cachedSession?.user;
      const resolvedUser = liveUser || cachedUser || null;

      setIsTelegram(hasInitData || hasInitDataUnsafe || hasPlatform || !!resolvedUser);

      // اعلام آمادگی به بله (تنها متد ضروری و امن)
      if (baleWebApp.ready) {
        baleWebApp.ready();
      } else {
        console.warn("baleWebApp.ready is not a function");
      }
      setIsReady(true);

      // دریافت اطلاعات کاربر
      if (resolvedUser) {
        setUser(resolvedUser);
        setCachedBaleSession({
          user: resolvedUser,
          initData: baleWebApp.initData || cachedSession?.initData || '',
          initDataUnsafe: baleWebApp.initDataUnsafe || cachedSession?.initDataUnsafe || {},
          platform: baleWebApp.platform || cachedSession?.platform || 'unknown',
        });
      }

      // دریافت start_param از URL (روش استاندارد)
      const urlParams = new URLSearchParams(window.location.search);
      const startParamValue = urlParams.get('tgWebAppStartParam');
      setStartParam(startParamValue);
      setIsStartCommand(!!startParamValue);

      // بررسی ادمین بودن (از طریق بک‌اند)
      if (resolvedUser?.id) {
        checkAdminStatus(resolvedUser.id);
      }

      // اعمال تم بله و به‌روزرسانی آن (با چک کردن وجود متدها)
      const updateTheme = () => {
        const colorScheme = baleWebApp.colorScheme || 'light';
        document.documentElement.setAttribute('data-theme', colorScheme);
        if (baleWebApp.themeParams) {
          setThemeParams(baleWebApp.themeParams);
        }
        document.body.style.backgroundColor = baleWebApp.themeParams?.bg_color || '#ffffff';
      };

      updateTheme();

      // فقط اگر onEvent وجود داشت، به تغییرات تم گوش بده
      if (baleWebApp.onEvent) {
        baleWebApp.onEvent('themeChanged', updateTheme);
      }

      return () => {
        if (baleWebApp.offEvent) {
          baleWebApp.offEvent('themeChanged', updateTheme);
        }
      };
    };

    // اگر SDK بله از قبل لود شده بود، مستقیماً راه‌اندازی کن، در غیر این صورت صبر کن
    if (window.Bale?.WebApp) {
      setupWebApp();
    } else {
      const interval = setInterval(() => {
        if (window.Bale?.WebApp) {
          clearInterval(interval);
          setupWebApp();
        }
      }, 50);
      return () => clearInterval(interval);
    }
  }, []);

  const checkAdminStatus = async (baleUserId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.kurromi.ir';
      const response = await fetch(`${apiUrl}/api/check-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_user_id: baleUserId }),
      });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  // شبیه‌سازی HapticFeedback (اختیاری، بدون خطا)
  const hapticFeedback = {
    impactOccurred: () => console.log("Haptic feedback would occur here"),
    notificationOccurred: () => console.log("Notification feedback would occur here"),
  };

  // شبیه‌سازی showAlert
  const showAlert = (message) => {
    if (webApp?.showAlert) {
      webApp.showAlert(message);
    } else {
      alert(message);
    }
  };

  return {
    WebApp: webApp,
    user,
    isReady,
    isAdmin,
    isStartCommand,
    startParam,
    theme: webApp?.colorScheme || 'light',
    themeParams,
    isTelegram,
    HapticFeedback: hapticFeedback,
    showAlert,
    checkAdminStatus,
  };
};
